import { fakeLocalToUtc } from '@modules/form-controls/types/timezone-convert';
import { toDisplayZoneWallClock } from '@modules/full-calendar/types/display-timezone-format';

/** What a grid handed the drop handler, before any frame is decided. */
export interface EmittedDropTimes {
  readonly start?: Date;
  readonly end?: Date;
}

/** What gets compared and persisted, each value in the frame its write path requires. */
export interface ResolvedDropTimes {
  readonly start?: Date;
  readonly end?: Date;
}

export interface DropFrameContext {
  /** True when the dragged events are all-day, so the drop denotes a calendar DATE, not a moment. */
  readonly allDay: boolean;
  /**
   * True for the month/year grids, whose cells ARE calendar days and which therefore emit an
   * account-zone wall clock ("fake local"); false for week/day, whose cells are moments and which
   * emit real instants.
   */
  readonly emitsAccountDayWallClock: boolean;
  /** The account (display) timezone — the only zone this app reckons days in. */
  readonly timeZone: string;
}

/**
 * SBUX-829 — resolve a dropped event's new boundaries into the frame the write path needs.
 *
 * The two grid families emit different KINDS of value and the two event kinds need different KINDS of
 * value, so a drop has four combinations and every one of them was previously decided inline. Both
 * bugs this replaces came from the same slip — using a value in a frame it was not in:
 *
 *  - an all-day drag in WEEK view read browser-local getters off a real instant, storing a two-day
 *    event from a one-day drag;
 *  - a timed drop on a MONTH cell stored the emitted wall clock verbatim, so the recorded instant was
 *    the browser's midnight rather than the account's — the stored value depended on the device.
 *
 * Extracted and exported so the decision is stated once and testable on its own. It is deliberately
 * pure: the caller supplies the zone and which grid emitted the value, because those are the two facts
 * the frame depends on and neither is recoverable from a bare `Date`.
 */
export function resolveDropTimes(emitted: EmittedDropTimes, context: DropFrameContext): ResolvedDropTimes {
  const convert = context.allDay ? toAllDayUtc : toInstant;
  return {
    start: emitted.start === undefined ? undefined : convert(emitted.start, context),
    end: emitted.end === undefined ? undefined : convert(emitted.end, context),
  };
}

/**
 * An all-day boundary: UTC midnight of the intended calendar day, so it reads as that same date in
 * every timezone. The browser-local getters are only meaningful once the value is an account-day wall
 * clock, hence the normalization first.
 */
function toAllDayUtc(date: Date, context: DropFrameContext): Date {
  const day = context.emitsAccountDayWallClock ? date : toDisplayZoneWallClock(date, context.timeZone);
  return new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate()));
}

/**
 * A timed boundary: the real instant the emitted value denotes. For month/year that means reading the
 * emitted wall clock in the ACCOUNT zone (the grid's `beginningOfDay`/`endOfDay` produced an
 * account-day wall clock); for week/day the emitted value already IS the instant.
 */
function toInstant(date: Date, context: DropFrameContext): Date {
  return context.emitsAccountDayWallClock ? fakeLocalToUtc(date, context.timeZone) : date;
}
